import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getUserById } from "@/api/auth/actions";
import { getAccessibleGroupsCached } from "@/api/dashboard/actions";
import { ROLES } from "@/configs";
import { hasPendingWorkspaceSelection } from "@/hooks/useSignupProvisioning";
import { logger } from "@/lib/logger";
import { isSignupSetupRoute } from "@/lib/signup/setupRouting";
// import { togglePopup } from "@/slices/Popup/popupReducer";
import { update } from "@/slices/auth/authReducer";
import { useDispatch } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { hasAnyRole } from "@/utils/permissions";
import { useRouter } from "next/router";

export const DashboardContext = createContext(null);

const toArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const normalizeApplicationCapabilities = (profile: Record<string, unknown>): string[] => {
  const rawCapabilities = Object.prototype.hasOwnProperty.call(profile, "application_capabilities")
    ? profile.application_capabilities
    : profile.applicationCapabilities;
  return toArray<unknown>(rawCapabilities).filter((capability): capability is string => typeof capability === "string");
};

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const maybeError = error as { name?: string; code?: number; message?: string };
  return (
    maybeError.name === "AbortError" ||
    maybeError.code === 20 ||
    maybeError.message === "AbortError" ||
    maybeError.message === "The operation was aborted."
  );
};

export const DashboardProvider = ({ children }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const user = useSelector((state) => state[rootReducerName.auth]?.user);
  const [role, setRole] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [targetId, setTargetId] = useState(user?.guid ? String(user.guid) : undefined);
  const [targetType, setTargetType] = useState<"Team" | "User" | null>("User");

  const [topDealViewMode, setTopDealViewMode] = useState(false);
  const [highValueViewMode, setHighValueViewMode] = useState(false);
  const [teamOnly, setTeamOnly] = useState(false);
  const activeProfileLoadRef = useRef<AbortController | null>(null);

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const setupRouteActive = isSignupSetupRoute(router.asPath || router.pathname);
  const pendingWorkspaceSelection = hasPendingWorkspaceSelection(user);

  const targetParams = (opts?: { includePipeline?: boolean }) => {
    // Use shape-agnostic role check; `user.role` may be number/string/array/null.
    const isManager = hasAnyRole(user, [ROLES.MANAGER]);
    const includePipeline = Boolean(opts?.includePipeline);

    const pipelineParams =
      includePipeline && pipelineId
        ? {
            pipeline_id: pipelineId
          }
        : {};

    // Return empty params if not a manager
    if (!isManager) {
      return includePipeline && pipelineId ? { params: { ...pipelineParams } } : {};
    }

    // New Team View Mode (Recursive)
    if (teamOnly) {
      return { params: { team_only: true, ...pipelineParams } };
    }

    // Handle team target case (Specific Group)
    if (targetType === "Team") {
      return { params: { group_id: targetId, ...pipelineParams } };
    }
    // Handle individual target case
    return {
      params: {
        user_id: targetId,
        group_id: groups?.[0]?.id,
        ...pipelineParams
      }
    };
  };

  const contextValue = useMemo(
    () => ({
      role,
      loading,
      targetId,
      setTargetId,
      targetType,
      setTargetType,
      groups,
      topDealViewMode,
      setTopDealViewMode,
      highValueViewMode,
      setHighValueViewMode,
      teamOnly,
      setTeamOnly,
      pipelineId,
      setPipelineId,
      targetParams
    }),
    [role, loading, targetId, targetType, groups, topDealViewMode, highValueViewMode, teamOnly, pipelineId]
  );

  const resetDashboardState = (nextTargetId?: string) => {
    setRole([]);
    setGroups([]);
    setLoading(false);
    setTargetId(nextTargetId);
    setTargetType("User");
    setTopDealViewMode(false);
    setHighValueViewMode(false);
    setTeamOnly(false);
  };

  useEffect(() => {
    activeProfileLoadRef.current?.abort();
    activeProfileLoadRef.current = null;

    const currentUserId = user?.guid ? String(user.guid) : undefined;

    if (!user) {
      resetDashboardState(undefined);
      return undefined;
    }

    setRole([]);
    setGroups([]);
    setTargetId(currentUserId);
    setTargetType("User");
    setTopDealViewMode(false);
    setHighValueViewMode(false);
    setTeamOnly(false);

    if (setupRouteActive || pendingWorkspaceSelection) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    activeProfileLoadRef.current = controller;
    setLoading(true);

    const fetchUser = async () => {
      try {
        const userRes: any = await getUserById(user.id, undefined, {
          suppressAuthLogoutOn401: true,
          signal: controller.signal
        });
        if (controller.signal.aborted) {
          return;
        }
        if (!userRes) {
          logger.error("Error fetching user data: user not found", { user_id: user.id });
          return;
        }
        const fetchedUser = userRes?.content ?? {};
        const fetchedGroupsRaw = toArray<any>(fetchedUser?.groups);
        const fetchedGroups = fetchedGroupsRaw.filter((item: any) => item && item.is_role === false);
        const fetchedRoles = fetchedGroupsRaw.filter((item: any) => item && item.is_role === true);
        const applicationCapabilities = normalizeApplicationCapabilities(fetchedUser);
        if (controller.signal.aborted) {
          return;
        }
        dispatch(
          update({
            user: {
              ...user,
              groups: fetchedGroups,
              role: fetchedRoles,
              tenant_stats: fetchedUser?.tenant_stats ?? {},
              preferences: fetchedUser?.preferences ?? {},
              applicationCapabilities,
              application_capabilities: applicationCapabilities
            }
          })
        );
        setRole(fetchedRoles);
        setGroups(fetchedGroups);

        if (fetchedRoles.find((role) => role.name == ROLES.MANAGER)) {
          const groupsRes: any = await getAccessibleGroupsCached({
            signal: controller.signal,
            suppressAuthLogoutOn401: true
          });
          if (controller.signal.aborted) {
            return;
          }
          const groups = groupsRes?.content ?? {};

          const accessibleGroups = toArray<any>(groups?.accessible_groups);
          setGroups(accessibleGroups);

          const accessibleTeamIds = accessibleGroups.map((g: any) => String(g.id));
          const accessibleUserIds = accessibleGroups.flatMap((g: any) =>
            toArray<any>(g?.members).map((m: any) => String(m.guid))
          );

          const currentTargetId = String(currentUserId || "");
          const isValidExistingTarget =
            (targetType === "Team" && accessibleTeamIds.includes(currentTargetId)) ||
            (targetType === "User" && accessibleUserIds.includes(currentTargetId));

          // Only apply defaults if there isn't already a valid selection.
          // Prefer current user if they're in the accessible list; otherwise first team.
          if (!isValidExistingTarget) {
            const canSelectSelf = currentTargetId && accessibleUserIds.includes(currentTargetId);
            if (canSelectSelf) {
              setTargetType("User");
              setTargetId(currentTargetId);
            } else {
              setTargetType("Team");
              setTargetId(accessibleGroups[0]?.id ? String(accessibleGroups[0].id) : undefined);
            }
            setTeamOnly(false);
          }
        }
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        logger.error("Error fetching user data:", error);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
        if (activeProfileLoadRef.current === controller) {
          activeProfileLoadRef.current = null;
        }
      }
    };

    void fetchUser();

    return () => {
      controller.abort();
      if (activeProfileLoadRef.current === controller) {
        activeProfileLoadRef.current = null;
      }
    };
  }, [pendingWorkspaceSelection, setupRouteActive, user?.guid]);

  return <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>;
};

export const useDashboard = () => useContext(DashboardContext);
